import { Button } from '@sunwebgroup/uds-ui/button'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import { isEmpty } from 'ramda'

import { use, useState } from 'react'

import { useIntl } from '@/intl'
import { useOptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/optimizely/useOptimizely'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { routesSelector } from '@/scopes/prices/components/selectors'
import { FlightCard } from '@/scopes/prices/components/TransportSelector/FlightSelector/FlightCard'
import { FlightCardNew } from '@/scopes/prices/components/TransportSelector/FlightSelector/FlightSelectorNew/FlightCardNew'
import { ArrivalTitle, DepartureTitle } from '@/scopes/prices/components/TransportSelector/FlightSelector/Headers'
import { createFlightComponentInteractionEvent } from '@/scopes/prices/domain/analytics'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import styles from './FlightSelectorMobile.module.css'

const STEPS = {
  selectDeparture: 'selectDeparture',
  selectReturn: 'selectReturn',
}

export function FlightSelectorMobile() {
  const { enableAlternateFlightVisualisation } = useOptimizelyFlagContext()
  const Card = enableAlternateFlightVisualisation ? FlightCardNew : FlightCard
  const intl = useIntl()
  const {
    setInboundRouteId,
    setOutboundRouteId,
  } = use(PricesActionsContext)

  const {
    outboundRoute,
    inboundRoute,
    inboundRoutes,
    outboundRoutes,
    selectedOutboundRouteId,
    selectedInboundRouteId,
  } = usePricesSelector(routesSelector)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [step, setStep] = useState<string | null>(null)
  const { sendEvent } = useAnalytics()
  const sheetContent
    = step === STEPS.selectDeparture
      ? `${intl.formatMessage({ id: 'prices_transport_selector_flight-selector_select_your_flight_outward' })} ${
        outboundRoute?.departureDate.fullDate
      }`
      : `${intl.formatMessage({ id: 'prices_transport_selector_flight-selector_select_your_flight_inward' })} ${
        inboundRoute?.arrivalDate.fullDate
      }`
  if (isEmpty(outboundRoutes) || isEmpty(inboundRoutes))
    return null

  const handleOpenModal = () => {
    setIsModalOpen(true)
    setStep(STEPS.selectDeparture)
    sendAnalyticsEvent('outbound')
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setStep(null)
  }

  function changeStep(text: string) {
    setStep(text)
    sendAnalyticsEvent(text === STEPS.selectDeparture ? 'outbound' : 'inbound')
  }

  function sendAnalyticsEvent(type: string) {
    sendEvent(createFlightComponentInteractionEvent({ type, result: 'Change flight' }))
  }

  const selectOutboundRoute = (id: string) => {
    setOutboundRouteId(id)
  }

  const selectInboundRoute = (id: string) => {
    setInboundRouteId(id)
  }

  return (
    <>
      <div className={styles.flightSelectorContainer}>
        <Typography className={styles.flightSelectorTitle} variant="display-f-regular">
          {intl.formatMessage({ id: 'prices_transport_selector_flight-selector_title' })}
        </Typography>
        <div className={styles.wrapper} id="FlightSelector">
          <div className={styles.section}>
            <DepartureTitle />
            {outboundRoute && <Card {...outboundRoute} isSelected noActiveState />}
          </div>
          <div className={styles.section}>
            <ArrivalTitle />
            {inboundRoute && <Card {...inboundRoute} isSelected noActiveState />}
          </div>
          <Button variant="secondary" onClick={handleOpenModal}>
            {intl.formatMessage({
              id: 'prices_transport_selector_flight-selector-mobile_button',
            })}
          </Button>
        </div>
        <Sheet.Root open={isModalOpen} onOpenChange={handleCloseModal}>
          <Sheet.Body side="right">
            <div className={styles.sheetCustomHeader}>
              <div className={styles.headerCell}>
                {step === STEPS.selectReturn && (
                  <button type="button" className={styles.returnWrapper} onClick={() => changeStep(STEPS.selectDeparture)}>
                    <Icon glyph="ChevronLeft" size={20}></Icon>
                    <Typography variant="body-m-bold-tight">
                      {intl.formatMessage({ id: 'prices_transport_selector_flight-selector_outward' })}
                    </Typography>
                  </button>
                )}
              </div>
              <div className={cn(styles.headerCell, styles.headerCellCenter)}>
                <Typography variant="body-m-bold-tight">
                  {step === STEPS.selectDeparture
                    ? intl.formatMessage({ id: 'prices_transport_selector_flight-selector_outward-flight' })
                    : intl.formatMessage({ id: 'prices_transport_selector_flight-selector-inward-flight' })}
                </Typography>
              </div>
              <div className={cn(styles.headerCell, styles.headerCellEnd)}>
                <Button variant="transparentPrimary" onClick={handleCloseModal}>
                  {intl.formatMessage({ id: 'prices_transport_selector_flight-selector_cancel' })}
                </Button>
              </div>
            </div>
            <Sheet.Content className={styles.sheetContent}>
              <Typography className={styles.sheetTitle} variant="body-m-regular-snug">{sheetContent}</Typography>
              {step === STEPS.selectDeparture && (
                <>
                  {outboundRoutes.map(route => (
                    <div className={styles.flightCardWrapper} key={route.id}>
                      <Card
                        {...route}
                        setSelected={selectOutboundRoute}
                        isSelected={selectedOutboundRouteId === route.id}
                      />
                    </div>
                  ))}
                </>
              )}
              {step === STEPS.selectReturn && (
                <>
                  {inboundRoutes.map(route => (
                    <div className={styles.flightCardWrapper} key={route.id}>
                      <Card
                        {...route}
                        setSelected={selectInboundRoute}
                        isSelected={selectedInboundRouteId === route.id}
                      />
                    </div>
                  ))}
                </>
              )}
              <div className={styles.buttonWrapper}>
                {step === STEPS.selectDeparture && (
                  <Button onClick={() => changeStep(STEPS.selectReturn)} fullWidth>
                    {intl.formatMessage({ id: 'prices_transport_selector_flight-selector_save_and_continue' })}
                  </Button>
                )}
                {step === STEPS.selectReturn && (
                  <Button onClick={handleCloseModal} fullWidth>
                    {intl.formatMessage({ id: 'prices_transport_selector_flight-selector-save' })}
                  </Button>
                )}
              </div>
            </Sheet.Content>
          </Sheet.Body>
        </Sheet.Root>
      </div>
    </>
  )
}
